import { TemplateSelectorModal } from '@salecycle/experimental/Modal/TemplateSelectorModal';
import { useFeatureFlags } from 'core/app/AppContext';
import { Template, TemplateType } from 'models/template/Template';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnsiteCampaignUrlParams } from 'sections/onsite-campaigns/hooks/useOnsiteCampaignUrlParams';
import { DeliveryMethod, TemplateTypes } from 'sections/onsite-campaigns/types';
import { useGetTemplates } from 'server/templates/queries/getTemplates/useGetTemplates';
import { useSendEmail } from './Workflows/SendEmail/hooks/useSendEmail';
import { useDisplayWebContent } from './Workflows/DisplayWebContent/hooks/useDisplayWebContent';

export interface OnsiteTemplateSelectorModalProps {
  open: boolean;
  onClose: () => void;
  messageId: string;
  location: 'sendEmail' | 'displayWebContent';
}

export const OnsiteTemplateSelectorModal = ({
  open,
  onClose,
  messageId,
  location,
}: OnsiteTemplateSelectorModalProps) => {
  const { t } = useTranslation('templateSelectModal');
  const { flagEnabled } = useFeatureFlags();
  const { siteId } = useOnsiteCampaignUrlParams();

  const { setTemplate: setSendEmailTemplate } = useSendEmail();
  const { setTemplate: setDisplayWebContentTemplate } = useDisplayWebContent();

  const query = useGetTemplates(siteId, {
    isDisplayOnlyEnabled: flagEnabled('ld-onsite-display-only'),
    showTemplatesValidForOffsiteOnly: location === 'sendEmail',
  });

  const templateTypes = useMemo(() => {
    const types = [TemplateType.DataAcquisition, TemplateType.SendMyBasket];

    if (flagEnabled('ld-onsite-display-only') && location !== 'sendEmail') {
      types.push(TemplateType.DisplayOnly);
    }

    return types;
  }, [flagEnabled, location]);

  const getTemplateThumbnailUrl = useCallback(
    (template: Template) =>
      template.channel === 'onsite'
        ? `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/sites/${siteId}/onsite/${template.id}/${template
            .supportedLanguages?.[0]?.language ?? ''}.html.jpg`
        : `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/sites/${siteId}/${template.id}/${template
            .supportedLanguages?.[0]?.language ?? ''}.template.jpg`,
    [siteId],
  );

  const onConfirm = useCallback(
    (template: Template) => {
      if (location === 'sendEmail') {
        setSendEmailTemplate({
          id: messageId,
          templateId: template.id,
          templateName: template.templateName,
          templateType: (template.type as unknown) as TemplateTypes,
          isEmbedded: template.isEmbedded,
          supportedLanguage: template.supportedLanguages?.[0]?.language ?? '',
        });
      } else {
        setDisplayWebContentTemplate({
          id: messageId,
          templateId: template.id,
          templateName: template.templateName,
          templateType: (template.type as unknown) as TemplateTypes,
          isEmbedded: template.isEmbedded,
          supportedLanguage: template.supportedLanguages?.[0]?.language ?? '',
        });
      }
    },
    [location, messageId, setDisplayWebContentTemplate, setSendEmailTemplate],
  );

  const channel = location === 'displayWebContent' ? DeliveryMethod.Onsite : DeliveryMethod.Offsite;
  const defaultTemplateTypeFilter = location === 'sendEmail' ? TemplateType.SendMyBasket : null;

  return (
    <TemplateSelectorModal
      open={open}
      onClose={onClose}
      t={t}
      templatesQuery={query}
      onConfirm={onConfirm}
      templateTypes={templateTypes}
      channel={channel}
      defaultTemplateTypeFilter={defaultTemplateTypeFilter}
      templateFallbackThumbnailUrl="/images/designs/design_gallery/default_template_placeholder_small.png"
      getTemplateThumbnailUrl={getTemplateThumbnailUrl}
      isEmbedFlagEnabled={flagEnabled('ld-embedded-content')}
    />
  );
};
